import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import KaizenTable from "@kaizen-ui/table";
import Button, { ButtonGroup } from "@kaizen-ui/button";

import { fetchIDMAccessCodeList } from "../../api";
import { Layout } from "../../layout";
import AccessibilityContext from "../../components/auth/UserPermissionContext";
import WithAuth from "../../components/auth/WithAuth";
import WithAdminUIAuth from "../../components/auth/WithAdminUIAuth";
import "./styles.css";

const Table = KaizenTable;

const IDMAccessCodeList = () => {
  const navigate = useNavigate();

  const [data, setData]               = useState([]);
  const [pageIndex, setPage]          = useState(0);
  const [pageSize, setSize]           = useState(50);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages]   = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [sorting, setSorting]         = useState([{ id: "modified_date", desc: true }]);
  const [filtering, setFiltering]     = useState([{ id: "test", value: "test" }]);
  const [fetching, setFetching]       = useState(true);

  const permissions = useContext(AccessibilityContext).filter(
    (x) => x.pagename === "user"
  )[0];

  const hiddenColumns = ["accesscodeid"];

  const columns = [
    { accessor: "accesscodeid" },
    { accessor: "accesscode",    Header: "Access Code"   },
    { accessor: "description",   Header: "Description"   },
    {
      accessor: "isactive",
      Header: "Is Active",
      // Fix: render boolean as "Yes" / "No" so false never shows as blank
      Cell: ({ value }) => (value === true || value === "true" ? "True" : "False"),
    },
  ];

  const IsArrayEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

  const handleFetchData = useCallback(
    (params) => {
      const inSortby   = JSON.parse(JSON.stringify(params.sortBy));
      const inFilterBy = JSON.parse(JSON.stringify(params.filters));

      if (!IsArrayEqual(inSortby, sorting))     setSorting(inSortby);
      if (!IsArrayEqual(inFilterBy, filtering)) setFiltering(inFilterBy);

      if (pageIndex !== params.pageIndex + 1 && currentPage !== params.pageIndex + 1) {
        setPage(params.pageIndex + 1);
      }
      if (pageSize !== params.pageSize) setSize(params.pageSize);
    },
    [currentPage, pageIndex, pageSize, sorting, filtering]
  );

  useEffect(() => {
    if (permissions?.read === false) {
      navigate("/unauthorized");
    }

    if (pageIndex > 0) {
      setFetching(true);
      fetchIDMAccessCodeList(pageIndex, pageSize, sorting, filtering).then((res) => {
        setData(res.contents ?? []);
        setCurrentPage(pageIndex);
        setTotalPages(Math.ceil((res.resultCount ?? 0) / pageSize));
        setTotalResults(res.resultCount ?? 0);
        setFetching(false);
      });
    }
  }, [pageIndex, pageSize, sorting, filtering, permissions?.read, navigate]);

  if (permissions?.read === false) {
    navigate("/unauthorized");
  }

  if (permissions?.read) {
    return (
      <Layout
        key="layidmaccesscode"
        title="IDM Access Code List"
        headerChildren={
          <ButtonGroup key="btngrpcreate">
            {permissions?.create ? (
              <Button
                key="btncreate"
                icon={{ name: "ActionsCircleAdd", variant: "solid" }}
                onClick={() => navigate("/idmaccesscode/0")}
              >
                Create IDM Access Code
              </Button>
            ) : (
              <div></div>
            )}
          </ButtonGroup>
        }
      >
        <Table
          key="tblidmaccesscode"
          initialState={{
            pageSize,
            pageIndex,
            hiddenColumns,
          }}
          paginationPageSizeOptions={[
            { label: "5",   value: 5   },
            { label: "10",  value: 10  },
            { label: "25",  value: 25  },
            { label: "50",  value: 50  },
            { label: "100", value: 100 },
          ]}
          rowOnClick={({ values }) =>
            navigate(`/idmaccesscode/${values.accesscodeid}`)
          }
          columns={columns}
          data={data}
          pageCount={totalPages}
          totalResults={totalResults}
          fetchData={handleFetchData}
          fetching={fetching}
          autoResetPage={false}
          autoResetSortBy={false}
          disableColumnHiding={true}
          disableExport={false}
          disableGlobalFilter={true}
        />
      </Layout>
    );
  }
};

export default WithAuth(WithAdminUIAuth(IDMAccessCodeList));